import * as React from 'react';
import { useEffect } from 'react';
import { Building2, Layers, Settings, Settings2, Users } from 'lucide-react';
import { createFileRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@hetivo/hetivo-lib-front-utils';
import { PageHeader, RouteTabs } from '@hetivo/hetivo-lib-web-utils';
import type { RouteTab } from '@hetivo/hetivo-lib-web-utils';
import { MainSelector } from '&/features/main-selector';
import { canAccessLocationSettings, canAccessTerritorySettings } from '&/features/settings/utils/settings-scope-access';
import { useMasterContext } from '&/master';

// ─── Constants ────────────────────────────────────────────────────────────────

const SETTINGS_ACCESS_PERMISSIONS = ['locations:list', 'territories:list', 'tenants:read'] as const;

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/_authenticated/settings')({
  beforeLoad: ({ context }): void => {
    const canAccessSettings = context.permissions.hasAnyPermission([...SETTINGS_ACCESS_PERMISSIONS]);
    if (context.permissions.isReady && !canAccessSettings) {
      throw redirect({ to: '/' });
    }
  },
  component: SettingsLayout,
});

// ─── Layout ───────────────────────────────────────────────────────────────────

function SettingsLayout(): React.JSX.Element {
  const { t } = useTranslation('settings');
  const permissions = usePermissions();
  const { hasPermission, isReady } = permissions;
  const { siteId, territoryId } = useMasterContext();
  const navigate = useNavigate();
  // Same predicates as the target routes' guards: navigating without them would bounce
  // back to /settings and re-trigger this effect in a loop.
  const canOpenLocation = isReady && canAccessLocationSettings(permissions);
  const canOpenTerritory = isReady && canAccessTerritorySettings(permissions);

  useEffect(() => {
    if (siteId && canOpenLocation) {
      void navigate({ params: { locationId: siteId }, to: '/settings/locations/$locationId' });
      return;
    }
    if (territoryId && canOpenTerritory) {
      void navigate({ params: { territoryId }, to: '/settings/territories/$territoryId' });
    }
  }, [siteId, territoryId, canOpenLocation, canOpenTerritory, navigate]);

  const tabs: RouteTab[] = [
    {
      icon: <Building2 className="h-4 w-4" />,
      label: t('settings.tabs.organization'),
      permission: 'tenants:read',
      to: '/settings/organization',
    },
    {
      icon: <Users className="h-4 w-4" />,
      label: t('settings.tabs.users'),
      permission: 'users:list',
      to: '/settings/users',
    },
    {
      icon: <Layers className="h-4 w-4" />,
      label: t('settings.tabs.territories'),
      permission: 'territories:list',
      to: '/settings/territories',
    },
    {
      icon: <Settings2 className="h-4 w-4" />,
      label: t('settings.tabs.defaults'),
      permission: 'tenant-defaults:read',
      to: '/settings/defaults',
    },
    {
      icon: <Settings className="h-4 w-4" />,
      label: t('settings.tabs.advanced'),
      permission: 'tenants:read',
      to: '/settings/advanced',
    },
  ];

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PageHeader breadcrumb={<MainSelector />} title={t('settings.title')} variant="banner" />
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="px-6 pb-3 pt-4">
          <RouteTabs ariaLabel={t('settings.title')} hasPermission={hasPermission} size="sm" tabs={tabs} />
        </div>
        <div className="flex flex-1 flex-col overflow-hidden px-6 pb-6 pt-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
